import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    FormControlLabel,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEditRole, useDeleteRole } from '../../hooks/useRole';
import { useRolePermissions, useAllPermissions } from '../../hooks/usePermission';

const extractEntityName = (permissionName) => {
    // Remove common action words from the start
    const actionWords = ['Create', 'View', 'Edit', 'Delete', 'Import', 'Export', 'Update', 'Approve', 'Reject'];
    let entityName = permissionName;

    // Find and remove the first action word
    for (const action of actionWords) {
        if (permissionName.startsWith(action)) {
            entityName = permissionName.slice(action.length);
            break;
        }
    }

    // If the entity name is plural, make it singular for grouping
    if (entityName.endsWith('s')) {
        entityName = entityName.slice(0, -1);
    }

    return entityName;
};

const groupPermissions = (permissions) => {
    const groups = {};

    permissions.forEach(permission => {
        // Extract entity name (e.g., "Events" from "ViewEvents")
        const entityMatch = permission.name.match(/[A-Z][a-z]+$/);
        const entity = entityMatch ? entityMatch[0] : 'Other';

        // Format permission name (e.g., "ViewEvents" -> "View Events")
        const formattedName = permission.name
            .replace(/([A-Z])/g, ' $1')
            .trim();

        if (!groups[entity]) {
            groups[entity] = [];
        }

        groups[entity].push({
            id: permission.id,
            name: permission.name,
            formattedName
        });
    });

    return groups;
};

const groupPermissionsByCategory = (permissions) => {
    const groups = {};

    permissions?.forEach(permission => {
        const name = permission.permissionName;
        const entityName = extractEntityName(name);

        // Format the permission name for display
        const displayName = name
            .replace(/([A-Z])/g, ' $1')
            .trim()
            .replace(/^./, str => str.toUpperCase());

        // Create category if it doesn't exist
        if (!groups[entityName]) {
            groups[entityName] = {
                permissions: [],
                description: `Manage ${entityName.toLowerCase()} related operations`
            };
        }

        // Add permission to the appropriate category
        groups[entityName].permissions.push({
            ...permission,
            displayName
        });
    });

    // Sort categories by name
    return Object.fromEntries(
        Object.entries(groups)
            .sort(([a], [b]) => a.localeCompare(b))
    );
};

export default function RoleSettings() {
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isViewPermissionsDialogOpen, setIsViewPermissionsDialogOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        permissions: []
    });
    const [validationError, setValidationError] = useState('');

    const { mutate: editRole, error: editError } = useEditRole();
    const { mutate: deleteRole, error: deleteError } = useDeleteRole();
    const { data: currentPermissions } = useRolePermissions({ roleId: selectedRole?.id });
    const { data: allPermissions } = useAllPermissions();

    // Group permissions for display
    const groupedPermissions = allPermissions ? groupPermissions(allPermissions) : {};

    useEffect(() => {
        if (selectedRole && currentPermissions) {
            setFormData({
                name: selectedRole.name || '',
                description: selectedRole.description || '',
                permissions: currentPermissions.map(p => p.permissionName) || []
            });
        }
    }, [selectedRole, currentPermissions]);

    useEffect(() => {
        const handleViewPermissions = (e) => {
            setSelectedRole(e.detail.role);
            setIsViewPermissionsDialogOpen(true);
        };

        const handleEditRole = (e) => {
            const role = e.detail.role;
            setSelectedRole(role);
            setFormData({
                name: role.name || '',
                description: role.description || '',
                permissions: [] // Will be updated by the other useEffect when currentPermissions loads
            });
            setIsEditDialogOpen(true);
        };

        const handleDeleteRole = (e) => {
            setSelectedRole(e.detail.role);
            setIsDeleteDialogOpen(true);
        };

        window.addEventListener('viewPermissions', handleViewPermissions);
        window.addEventListener('editRole', handleEditRole);
        window.addEventListener('deleteRole', handleDeleteRole);

        return () => {
            window.removeEventListener('viewPermissions', handleViewPermissions);
            window.removeEventListener('editRole', handleEditRole);
            window.removeEventListener('deleteRole', handleDeleteRole);
        };
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value || ''
        }));
    };

    const handlePermissionChange = (permissionName) => {
        setFormData(prev => {
            const permissions = prev.permissions.includes(permissionName)
                ? prev.permissions.filter(p => p !== permissionName)
                : [...prev.permissions, permissionName];
            return {
                ...prev,
                permissions
            };
        });
    };

    const handleGroupToggle = (entity, checked) => {
        setFormData(prev => {
            const groupPermissions = groupedPermissions[entity].map(p => p.name);
            const newPermissions = checked
                ? [...new Set([...prev.permissions, ...groupPermissions])]
                : prev.permissions.filter(p => !groupPermissions.includes(p));

            return {
                ...prev,
                permissions: newPermissions
            };
        });
    };

    const isGroupFullySelected = (entity) => {
        const groupPermissions = groupedPermissions[entity].map(p => p.name);
        return groupPermissions.every(p => formData.permissions.includes(p));
    };

    const isGroupPartiallySelected = (entity) => {
        const groupPermissions = groupedPermissions[entity].map(p => p.name);
        const selectedCount = groupPermissions.filter(p => formData.permissions.includes(p)).length;
        return selectedCount > 0 && selectedCount < groupPermissions.length;
    };

    const handleSave = () => {
        setValidationError('');

        // Basic validation
        if (!formData.name.trim()) {
            setValidationError('Role name is required');
            return;
        }

        // Ensure we have valid data before saving
        const roleData = {
            name: formData.name.trim(),
            description: formData.description.trim(),
            permissions: formData.permissions || []
        };

        editRole({
            id: selectedRole.id,
            data: roleData
        }, {
            onSuccess: () => {
                setIsEditDialogOpen(false);
            },
            onError: (error) => {
                setValidationError(error.message || 'An error occurred while updating the role');
            }
        });
    };

    const handleDelete = () => {
        deleteRole(selectedRole.id, {
            onSuccess: () => {
                setIsDeleteDialogOpen(false);
            },
            onError: (error) => {
                setValidationError(error.message || 'An error occurred while deleting the role');
            }
        });
    };

    return (
        <>
            {/* View Permissions Dialog */}
            <Dialog
                open={isViewPermissionsDialogOpen}
                onClose={() => setIsViewPermissionsDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle className="bg-blueColor text-white py-4">
                    <i className="fas fa-eye text-xl mx-2"></i>
                    <span className="text-xl font-semibold">Role Permissions - {selectedRole?.name}</span>
                </DialogTitle>
                <DialogContent className="mt-4">
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Category</TableCell>
                                    <TableCell>Permission Name</TableCell>
                                    <TableCell>Description</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.entries(groupPermissionsByCategory(currentPermissions)).map(([category, { permissions, description }]) => (
                                    permissions.length > 0 && (
                                        <React.Fragment key={category}>
                                            <TableRow>
                                                <TableCell
                                                    colSpan={3}
                                                    className="bg-gray-50 font-semibold"
                                                >
                                                    <b>{category}</b> - <span className='font-bold text-gray-500'>{description}</span>
                                                </TableCell>
                                            </TableRow>
                                            {permissions.map((permission) => (
                                                <TableRow key={permission.id}>
                                                    <TableCell>{category}</TableCell>
                                                    <TableCell>{permission.displayName}</TableCell>
                                                    <TableCell>
                                                        {permission.permissionName}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </React.Fragment>
                                    )
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsViewPermissionsDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog
                open={isEditDialogOpen}
                onClose={() => setIsEditDialogOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle className="bg-blueColor text-white py-4">
                    <i className="fas fa-edit text-xl mx-2"></i>
                    <span className="text-xl font-semibold">Edit Role</span>
                </DialogTitle>
                <DialogContent className="mt-4">
                    {(validationError || editError) && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {validationError || editError?.message}
                        </Alert>
                    )}
                    <div className="grid grid-cols-1 gap-6 p-6">
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Name
                            </label>
                            <input
                                name="name"
                                value={formData.name || ''}
                                onChange={handleInputChange}
                                type="text"
                                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                            />
                        </div>

                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Description
                            </label>
                            <textarea
                                name="description"
                                value={formData.description || ''}
                                onChange={handleInputChange}
                                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                                rows="4"
                            />
                        </div>

                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Permissions
                            </label>
                            <Box className="mt-2 max-h-[400px] overflow-y-auto">
                                {Object.entries(groupedPermissions).map(([entity, permissions]) => (
                                    <Accordion key={entity} className="mb-2">
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            className="bg-gray-50"
                                        >
                                            <FormControlLabel
                                                onClick={(e) => e.stopPropagation()}
                                                control={
                                                    <Checkbox
                                                        checked={isGroupFullySelected(entity)}
                                                        indeterminate={isGroupPartiallySelected(entity)}
                                                        onChange={(e) => handleGroupToggle(entity, e.target.checked)}
                                                        className="text-blueColor"
                                                    />
                                                }
                                                label={
                                                    <Typography className="font-semibold">
                                                        {entity}
                                                    </Typography>
                                                }
                                            />
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box className="grid grid-cols-1 gap-2 pl-4">
                                                {permissions.map((permission) => (
                                                    <FormControlLabel
                                                        key={permission.id}
                                                        control={
                                                            <Checkbox
                                                                checked={formData.permissions.includes(permission.name)}
                                                                onChange={() => handlePermissionChange(permission.name)}
                                                                className="text-blueColor"
                                                            />
                                                        }
                                                        label={permission.formattedName}
                                                        className="hover:bg-gray-50 p-2 rounded"
                                                    />
                                                ))}
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Box>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions className="p-6">
                    <Button
                        onClick={() => setIsEditDialogOpen(false)}
                        className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        className="px-4 py-2 bg-blueColor text-white rounded"
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
                <DialogTitle className="bg-red-600 text-white py-4">
                    <i className="fas fa-trash text-xl mx-2"></i>
                    <span className="text-xl font-semibold">Delete Role</span>
                </DialogTitle>
                <DialogContent className="mt-4">
                    {(validationError || deleteError) && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {validationError || deleteError?.message}
                        </Alert>
                    )}
                    <p className="text-gray-700">Are you sure you want to delete this role?</p>
                </DialogContent>
                <DialogActions className="p-4">
                    <Button
                        onClick={() => setIsDeleteDialogOpen(false)}
                        className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        className="px-4 py-2 bg-red-600 text-white rounded"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
} 